'use client';

import { useContext, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage as Image, DotSpinner } from '..';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { tagBundle } from 'components/shared/Cart/bundle';
import { ProductType } from 'components/shared/Cart/types';
import { getTaxString } from 'components/shared/Cart/utils';
import { ToastContext } from 'components/shared/Toast';
import { useClickOutside } from 'hooks/dom/useClickOutside';
import { getCurrencySymbolFromString } from 'lib/region/countryUtils';
import { Currency } from 'utils/Currency';
import { formatPriceWithComma } from 'hooks/pricing/useFormattedPrice';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  selectionKey,
  type PackColorOption,
  type PackFrameOption,
  type PackOffer,
  type PackSizeOption,
} from 'content/products/packs/types';
import styles from './PackCard.module.css';
import {
  ShopProductCardRoot,
  ShopCardBody,
  ShopCardBottomSheet,
  ShopCardFooterRow,
  ShopCardMedia,
  ShopCardMediaDimBackdrop,
  ShopCardPriceCluster,
  ShopCardPrimaryCta,
  ShopCardSpecSection,
  ShopCardTag,
  ShopCardTagText,
} from './shopProductCard';

type OverlayStep = 'closed' | 'color' | 'size' | 'frame';

// getCurrencySymbolFromString falls back to '$' for any code outside `Currency`,
// which would advertise a CHF price in dollars; show the ISO code instead.
const hasCurrencySymbol = (code: string) =>
  (Object.values(Currency) as string[]).includes(code.toLowerCase());

const formatOfferPrice = (currencyCode: string, price: number) => {
  const currency = currencyCode.toLowerCase() as Currency;
  return hasCurrencySymbol(currencyCode)
    ? `${getCurrencySymbolFromString(currencyCode)}${formatPriceWithComma(price, currency)}`
    : `${currencyCode.toUpperCase()} ${formatPriceWithComma(price, currency)}`;
};

/** Invariant: nothing here may import ring/accessory maps — `offer` is resolved. */
export const PackCard = ({ offer }: { offer: PackOffer }) => {
  const CartClient = useCartStore(
    useShallow((s) => ({
      store: s.store,
      taxString: s.taxString,
      addToCart: s.addToCart,
    })),
  );
  const { showToast } = useContext(ToastContext);
  const t = useTranslations('ring');
  const tCart = useTranslations('cart');

  const needsFrame = offer.frames.length > 0;
  const defaultColorKey = offer.colors[0]?.key ?? null;
  const [step, setStep] = useState<OverlayStep>('closed');
  const [selectedColorKey, setSelectedColorKey] = useState<string | null>(
    defaultColorKey,
  );
  const [selectedSize, setSelectedSize] = useState<PackSizeOption | null>(null);
  const [selectedFrameKey, setSelectedFrameKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  const isOpen = step !== 'closed';

  const closeOverlay = () => {
    setStep('closed');
    setSelectedColorKey(defaultColorKey);
    setSelectedSize(null);
    setSelectedFrameKey(null);
  };

  useClickOutside(overlayRef, () => setTimeout(closeOverlay, 200), isOpen);

  const localized = (key: string, fallback: string) =>
    t.has(key) ? t(key) : fallback;
  const prettify = (key: string) =>
    key.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const title = localized(`packs.items.${offer.id}.title`, offer.title);
  const description = localized(
    `packs.items.${offer.id}.description`,
    offer.description,
  );
  const colorLabel = (c: PackColorOption) =>
    localized(`ring.productSelection.${c.key}`, prettify(c.name));
  const frameLabel = (f: PackFrameOption) =>
    localized(`packs.frames.${f.key}`, f.label);

  const priceLabel = formatOfferPrice(offer.currencyCode, offer.price);
  const resolvedTaxString =
    CartClient.taxString || getTaxString(offer.store as ShopifyStore, tCart);

  const selectedColor =
    offer.colors.find((c) => c.key === selectedColorKey) ?? null;
  // The grid is offer.sizeUnion so it never reflows; unstocked sizes go disabled.
  const buyableSizes = new Set(
    (selectedColor?.sizes ?? []).map((s) => String(s.size)),
  );
  const openSize = selectedColor?.sizes.find((s) => s.isOpenSize) ?? null;

  const handleAddToCart = async () => {
    if (!selectedColor || !selectedSize) return;
    const frameKey = needsFrame ? selectedFrameKey : null;
    if (needsFrame && !frameKey) return;

    const variantId =
      offer.variants[
        selectionKey(selectedColor.key, selectedSize.size, frameKey)
      ];
    if (!variantId) {
      showToast(t('packs.unavailableToast'));
      return;
    }

    // tagBundle marks the LAST line `_is_bundle_parent`, so the pack must go last
    // and the sizing kit first — removal cascades from the parent.
    const lines = [
      ...(selectedSize.isOpenSize && offer.sizingKitVariantId
        ? [
            {
              shopifyVariantId: offer.sizingKitVariantId,
              productType:
                offer.ringFamily === 'pro'
                  ? ProductType.RING_PRO
                  : ProductType.RING,
            },
          ]
        : []),
      {
        shopifyVariantId: variantId,
        productType: ProductType.GENERIC,
        params: {
          noteAttributes: [
            { key: '_bundle', value: offer.id },
            { key: '_ring_color', value: selectedColor.key },
            {
              key: '_ring_size',
              value: selectedSize.isOpenSize
                ? 'OPEN'
                : String(selectedSize.size),
            },
            ...(frameKey ? [{ key: '_blbg_frame', value: frameKey }] : []),
          ],
        },
      },
    ];

    try {
      setIsLoading(true);
      await CartClient.addToCart(tagBundle(lines));
      closeOverlay();
    } catch (err) {
      console.error('Error adding pack to cart:', err);
      showToast(t('packs.addToCartErrorToast'));
    } finally {
      setIsLoading(false);
    }
  };

  const isSoldOut = offer.tag === 'sold out';

  // A cart opened against another store must never receive this store's GIDs.
  if (CartClient.store !== (offer.store as ShopifyStore)) return null;

  return (
    <ShopProductCardRoot className={styles.root}>
      <ShopCardMedia as="div" $background="white" onClick={closeOverlay}>
        {offer.imgSrc && (
          <Image
            src={offer.imgSrc}
            alt={title}
            height={448}
            width={300}
            sizes="(min-width: 1024px) calc((100% - 48px) / 3), 100vw"
            style={{
              objectFit: 'cover',
              width: '100%',
              borderRadius: '12px',
            }}
          />
        )}
      </ShopCardMedia>
      <ShopCardBody>
        <ShopCardSpecSection className={`ringSpec ${styles.ringSpec}`}>
          <h3 className="title">{title}</h3>
          {description && <p className="description">{description}</p>}
          <br />
        </ShopCardSpecSection>
        <ShopCardFooterRow
          aria-hidden={isOpen}
          style={isOpen ? { visibility: 'hidden' } : undefined}
        >
          <ShopCardPriceCluster
            className={`pricing-info ${styles.pricingInfo}`}
          >
            {priceLabel}
            <span>{resolvedTaxString}</span>
          </ShopCardPriceCluster>
          <ShopCardPrimaryCta
            data-buttontype="show color"
            data-product={offer.title}
            className={isSoldOut ? 'quickAdd disable' : 'quickAdd'}
            onClick={() => !isSoldOut && setStep('color')}
            disabled={isSoldOut}
          >
            {t('packs.addToCart')}
          </ShopCardPrimaryCta>
        </ShopCardFooterRow>
      </ShopCardBody>
      {offer.tag ? <ShopCardTag><ShopCardTagText tag={offer.tag} /></ShopCardTag> : null}
      <ShopCardMediaDimBackdrop className={isOpen ? 'open' : ''} />
      <ShopCardBottomSheet
        className={`sizeOverlay ${isOpen ? 'open' : ''} ${styles.bottomSheet}`}
        ref={overlayRef}
        aria-hidden={!isOpen}
      >
        <div className="stepContent" key={step === 'closed' ? 'color' : step}>
          {(step === 'color' || step === 'closed') && (
            <>
              <div className="stepHeader">
                <div className="heading">{t('packs.color')}</div>
              </div>
              <div className="colorSwatches">
                {offer.colors.map((c) => (
                  <button
                    key={c.key}
                    className={[
                      'color-swatch-btn',
                      c.swatch ? '' : 'labelled',
                      selectedColorKey === c.key ? 'active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    data-product={offer.title}
                    data-buttontype="select color"
                    onClick={() => {
                      setSelectedColorKey(c.key);
                      // A size the new colour does not stock must not survive.
                      setSelectedSize(null);
                    }}
                    aria-label={colorLabel(c)}
                    aria-pressed={selectedColorKey === c.key}
                  >
                    {c.swatch ? (
                      <Image
                        src={c.swatch}
                        alt={colorLabel(c)}
                        width={48}
                        height={48}
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      colorLabel(c)
                    )}
                  </button>
                ))}
              </div>
              {selectedColor && (
                <div className="selectedColorName">
                  {colorLabel(selectedColor)}
                </div>
              )}
              <button
                className="addToCart"
                data-product={offer.title}
                data-buttontype="go to size step"
                disabled={!selectedColor || isLoading}
                onClick={() => setStep('size')}
              >
                {t('packs.selectSize')}
              </button>
            </>
          )}
          {step === 'size' && selectedColor && (
            <>
              <div className="stepHeader">
                <button
                  className="overlayBack"
                  onClick={() => setStep('color')}
                  data-buttontype="back to color"
                  aria-label={t('packs.back')}
                >
                  ←
                </button>
                <div className="heading">{t('packs.ringSize')}</div>
              </div>
              <div className="sizes">
                {offer.sizeUnion
                  .filter((s) => !s.isOpenSize)
                  .map((s) => {
                    const isSelected =
                      String(selectedSize?.size) === String(s.size);
                    return (
                      <button
                        key={String(s.size)}
                        className={isSelected ? 'selectedRingSize' : ''}
                        data-product={offer.title}
                        data-buttontype="select size"
                        disabled={!buyableSizes.has(String(s.size))}
                        onClick={() => setSelectedSize(s)}
                        aria-pressed={isSelected}
                      >
                        {String(s.size)}
                      </button>
                    );
                  })}
              </div>
              {offer.sizingKitVariantId && openSize && (
                <div className="sizingKit">
                  <h4>{t('packs.sizingKitPrompt')}</h4>
                  <button
                    data-product={offer.title}
                    data-buttontype="select sizing kit"
                    className={
                      selectedSize?.isOpenSize ? 'selectedRingSize' : ''
                    }
                    onClick={() => setSelectedSize(openSize)}
                    aria-pressed={!!selectedSize?.isOpenSize}
                  >
                    <Image
                      src="web_v2/ring-buy/sizing-kit-1.png"
                      width={50}
                      height={50}
                      alt={t('packs.sizingKitImageAlt')}
                      style={{ objectFit: 'cover' }}
                    />
                    <div className="content">
                      <div className="title">{t('packs.sizingKitTitle')}</div>
                      <div className="subtitle">
                        {t('packs.sizingKitSubtitle')}
                      </div>
                    </div>
                  </button>
                </div>
              )}
              <button
                className="addToCart"
                data-product={offer.title}
                data-buttontype={needsFrame ? 'go to frame step' : 'add to cart'}
                disabled={!selectedSize || isLoading}
                onClick={() =>
                  needsFrame ? setStep('frame') : handleAddToCart()
                }
                aria-busy={!needsFrame && isLoading}
              >
                {needsFrame ? (
                  t('packs.selectFrame')
                ) : isLoading ? (
                  <DotSpinner color="#FFFFFF" />
                ) : (
                  t('packs.addToCartForPrice', { price: priceLabel })
                )}
              </button>
            </>
          )}
          {step === 'frame' && (
            <>
              <div className="stepHeader">
                <button
                  className="overlayBack"
                  onClick={() => setStep('size')}
                  data-buttontype="back to size"
                  aria-label={t('packs.back')}
                >
                  ←
                </button>
                <div className="heading">{t('packs.frameHeading')}</div>
              </div>
              <div className="variantSelect">
                <div className="variantOptions frameOptions">
                  {offer.frames.map((f) => (
                    <button
                      key={f.key}
                      className={`variantBtn${
                        selectedFrameKey === f.key ? ' selected' : ''
                      }`}
                      data-product={offer.title}
                      data-buttontype="select frame"
                      aria-pressed={selectedFrameKey === f.key}
                      onClick={() => setSelectedFrameKey(f.key)}
                    >
                      <Image
                        src={f.previewImg}
                        // Decorative: variantLabel below is the accessible name.
                        alt=""
                        width={160}
                        height={64}
                        style={{
                          width: '100%',
                          height: '64px',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                      <span className="variantLabel">{frameLabel(f)}</span>
                    </button>
                  ))}
                </div>
              </div>
              <button
                className="addToCart"
                data-buttontype="add to cart"
                data-product={offer.title}
                onClick={handleAddToCart}
                disabled={selectedFrameKey === null || isLoading}
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <DotSpinner color="#FFFFFF" />
                ) : (
                  t('packs.addToCartForPrice', { price: priceLabel })
                )}
              </button>
            </>
          )}
        </div>
      </ShopCardBottomSheet>
    </ShopProductCardRoot>
  );
};
